// src/components/Header.jsx
import React, { useEffect, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import { motion } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import Logo from "../images/logo.png";
import { Mail, Clipboard, Briefcase, Users } from "lucide-react";

/* ---------------------------
   Theme-driven dark header styles
   --------------------------- */
const HeaderWrap = styled.header`
  background: ${(p) =>
    p.theme.gradients?.bgHeader ||
    `linear-gradient(90deg, ${p.theme.colors?.bgDark || "#071024"} 0%, ${
      p.theme.colors?.bgDarker || "#07182a"
    } 100%)`};
  position: sticky;
  top: 0;
  z-index: 60;
  border-bottom: 1px solid
    ${(p) => p.theme.colors?.borderLight || "rgba(255, 255, 255, 0.04)"};
  color: ${(p) => p.theme.colors?.text || "#e6f0ff"};
`;

/* Use grid so we can position logo / center nav / CTA precisely */
const HeaderInner = styled.div`
  max-width: ${(p) => p.theme.maxWidth || "1200px"};
  margin: 0 auto;
  padding: 12px 16px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 16px;
  position: relative;

  @media (min-width: 900px) {
    padding: 14px 20px;
  }
`;

/* Brand wrapper */
const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-self: start;
`;

/* Image logo */
const LogoImg = styled.img`
  width: 100px;
  height: 60px;
  border-radius: 8px;
  object-fit: contain;
  display: block;

  @media (max-width: 899px) {
    width: 100px;
    height: 60px;
  }
`;

/* Fallback initials box if image fails to load */
const LogoFallback = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: ${(p) =>
    p.theme.gradients?.primary ||
    `linear-gradient(90deg, ${p.theme.colors?.primary || "#F5C542"}, ${
      p.theme.colors?.primaryDark || "#F29E20"
    })`};
  display: grid;
  place-items: center;
  color: ${(p) => p.theme.colors?.text || "#fff"};
  font-weight: 800;

  @media (max-width: 899px) {
    width: 44px;
    height: 44px;
  }
`;

/* Brand text block (hidden on small screens) */
const BrandText = styled.div`
  display: block;
  line-height: 1;

  .brand-name {
    font-weight: 800;
    color: ${(p) => p.theme.colors?.text || "#e8f3ff"};
    font-size: 25px;
  }
  .brand-tag {
    font-size: 16px;
    margin-top: 4px;
    color: ${(p) => p.theme.colors?.muted || "rgba(232, 243, 255, 0.7)"};
  }

  @media (max-width: 899px) {
    .brand-name {
      font-weight: 800;
      color: ${(p) => p.theme.colors?.text || "#e8f3ff"};
      font-size: 17px;
    }
    .brand-tag {
      font-size: 12px;
      margin-top: 5px;
      color: ${(p) => p.theme.colors?.muted || "rgba(232, 243, 255, 0.7)"};
    }
  }
`;

/* Desktop nav (center column). Center it with justify-self */
const DesktopNav = styled.nav`
  display: none;
  align-items: center;
  position: relative;
  justify-self: center;
  width: 100%;
  max-width: 640px;

  @media (min-width: 900px) {
    display: flex;
  }
`;

/* nav list centered inside nav */
const NavList = styled.ul`
  display: flex;
  gap: 18px;
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
  justify-content: center;
  width: 100%;
`;

/* individual link */
const NavItem = styled.li`
  position: relative;
`;
const DesktopNavLink = styled(NavLink)`
  color: ${(p) => p.theme.colors?.muted || "rgba(230,240,255,0.8)"};
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 8px 10px;
  border-radius: 8px;
  display: inline-block;
  cursor: pointer;

  &.active {
    color: ${(p) => p.theme.colors?.text || "#fff"};
  }

  &:hover,
  &:focus {
    color: ${(p) => p.theme.colors?.text || "#fff"};
  }

  &:focus {
    outline: none;
  }
`;

/* Framer Motion indicator (positioned relative to nav list) */
const Indicator = styled(motion.div)`
  position: absolute;
  height: 3px;
  bottom: -6px;
  left: 0;
  background: ${(p) =>
    p.theme.gradients?.accent ||
    `linear-gradient(90deg, ${p.theme.colors?.accent || "#4A00E0"}, ${
      p.theme.colors?.accentLight || "#0575E6"
    })`};
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(13, 132, 255, 0.12);
  will-change: transform, width;
`;

/* Attractive Contact CTA (right column) - hidden on small screens */
/* We'll render this as a react-router Link using `as={Link}` when used */
const ContactCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${(p) =>
    p.theme.gradients?.cta ||
    `linear-gradient(90deg, ${p.theme.colors?.secondary || "#E63946"}, ${
      p.theme.colors?.secondaryDark || "#FF3C2F"
    })`};
  color: ${(p) => p.theme.colors?.ctaText || "#021025"};
  padding: 8px 14px;
  margin-left: 20px;
  border-radius: 999px;
  font-weight: 800;
  text-decoration: none;
  box-shadow: ${(p) =>
    p.theme.shadows?.cta || "0 12px 34px rgba(13, 132, 255, 0.12)"};
  transition: transform 180ms ease, box-shadow 180ms ease, opacity 150ms ease;
  white-space: nowrap;
  justify-self: end;

  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: ${(p) =>
      p.theme.shadows?.ctaHover || "0 18px 44px rgba(13, 132, 255, 0.18)"};
  }

  .cta-icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: ${(p) => p.theme.colors?.text || "rgba(255,255,255,0.9)"};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${(p) => p.theme.colors?.ctaIconStroke || "#021025"};
  }

  .cta-text {
    display: flex;
    flex-direction: column;
    line-height: 1;
    text-align: left;
  }

  .cta-line1 {
    font-size: 0.85rem;
    color: white;
  }
  .cta-line2 {
    font-size: 0.75rem;
    opacity: 0.95;
    color: white;
    font-weight: 700;
  }

  @media (max-width: 899px) {
    display: none; /* hide CTA on small screens */
  }
`;

/* Mobile CTA (visible inside mobile menu) */
const ContactCTA2 = styled(ContactCTA)`
  display: flex;
  @media (min-width: 900px) {
    display: none;
  }
  width: 200px;
`;

/* Mobile controls (visible only on small screens) */
const MobileControls = styled.div`
  display: none;
  align-items: center;
  gap: 8px;
  justify-self: end;

  @media (max-width: 899px) {
    display: flex;
  }
`;

const NavButton = styled.button`
  background: transparent;
  border: 0;
  padding: 8px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(p) => p.theme.colors?.muted || "rgba(230,240,255,0.8)"};
`;

/* Mobile menu dropdown (no CTA inside) */
const MobileMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  width: calc(100% - 24px);
  left: 0;
  right: 0;
  margin: 0 auto;
  background: ${(p) =>
    p.theme.gradients?.mobileMenuBg ||
    `linear-gradient(180deg, ${p.theme.colors?.bgDark || "#071024"}, ${
      p.theme.colors?.bgDarker || "#051223"
    })`};
  border-radius: 12px;
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.6);
  padding: 12px;
  display: ${(p) => (p.open ? "block" : "none")};
  color: ${(p) => p.theme.colors?.text || "#e8f3ff"};
  border: 1px solid rgba(255, 255, 255, 0.03);

  @media (min-width: 900px) {
    display: none;
  }
`;

/* Mobile link with icon */
const MobileLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-radius: 8px;
  text-decoration: none;
  color: ${(p) => p.theme.colors?.text || "#e8f3ff"};
  font-weight: 700;
  transition: background 120ms ease, color 120ms ease;

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    color: ${(p) => p.theme.colors?.muted || "rgba(230,240,255,0.8)"};
  }

  &:hover,
  &:focus {
    background: rgba(255, 255, 255, 0.02);
    color: #fff;
    outline: none;
  }
`;

/* ---------------------------
   Component
   --------------------------- */
export default function Header() {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const location = useLocation();

  // nav links (center) - routes using react-router
  const links = [
    { id: "home", label: "Home", href: "/" },
    { id: "about", label: "About Us", href: "/about" },
    { id: "team", label: "Our Team", href: "/team" },
    { id: "contact", label: "Contact Us", href: "/contact" },
  ];

  // mobile icons for these pages
  const mobileIcons = [Clipboard, Briefcase, Users, Mail];

  // indicator state
  const navRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0,
    opacity: 0,
  });
  const [activeIndex, setActiveIndex] = useState(0);

  // logo load error handling
  const [logoError, setLogoError] = useState(false);

  // close mobile menu on resize > tablet
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 900) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // update activeIndex when location changes
  useEffect(() => {
    const idx = links.findIndex((l) =>
      l.href === "/"
        ? location.pathname === "/"
        : location.pathname.startsWith(l.href)
    );
    setActiveIndex(idx === -1 ? 0 : idx);
  }, [location.pathname]); // eslint-disable-line

  // measure indicator position
  useEffect(() => {
    const init = () => {
      if (!navRef.current) return;
      const anchors = navRef.current.querySelectorAll("a");
      const node = anchors[activeIndex];
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const parentRect = navRef.current.getBoundingClientRect();
      setIndicatorStyle({
        left: rect.left - parentRect.left,
        width: rect.width,
        opacity: 1,
      });
    };
    init();
    window.addEventListener("load", init);
    window.addEventListener("resize", init);
    return () => {
      window.removeEventListener("load", init);
      window.removeEventListener("resize", init);
    };
  }, [activeIndex]);

  const moveIndicatorTo = (el) => {
    if (!navRef.current || !el) return;
    const rect = el.getBoundingClientRect();
    const parentRect = navRef.current.getBoundingClientRect();
    setIndicatorStyle({
      left: rect.left - parentRect.left,
      width: rect.width,
      opacity: 1,
    });
  };

  const resetToActive = () => {
    const anchors = navRef.current?.querySelectorAll("a");
    const node = anchors?.[activeIndex];
    if (node) moveIndicatorTo(node);
    else setIndicatorStyle((s) => ({ ...s, opacity: 0 }));
  };

  return (
    <HeaderWrap>
      <HeaderInner className="container">
        {/* LEFT: logo + (hidden) name on mobile */}
        <Brand>
          {!logoError ? (
            <LogoImg
              src={Logo}
              alt="K Shyam G Management logo"
              onError={() => setLogoError(true)}
            />
          ) : (
            <LogoFallback aria-hidden>KG</LogoFallback>
          )}

          <BrandText>
            <div className="brand-name">K Shyam G Management</div>
            <div className="brand-tag">
              Trusted collections & risk solutions
            </div>
          </BrandText>
        </Brand>

        {/* CENTER: nav */}
        <DesktopNav aria-label="Main navigation">
          <NavList
            ref={navRef}
            onMouseLeave={resetToActive}
            onBlur={resetToActive}
          >
            {links.map((l, i) => (
              <NavItem key={l.id}>
                <DesktopNavLink
                  to={l.href}
                  end={l.href === "/"} // exact for home
                  onMouseEnter={(e) => moveIndicatorTo(e.currentTarget)}
                  onFocus={(e) => moveIndicatorTo(e.currentTarget)}
                  onClick={() => {
                    setActiveIndex(i);
                    // close mobile just in case (no-op on desktop)
                    setOpen(false);
                  }}
                >
                  {l.label}
                </DesktopNavLink>
              </NavItem>
            ))}

            <Indicator
              layout
              initial={{ opacity: 0 }}
              animate={{
                left: indicatorStyle.left,
                width: indicatorStyle.width,
                opacity: indicatorStyle.opacity,
              }}
              transition={{ type: "spring", stiffness: 260, damping: 28 }}
            />
          </NavList>
        </DesktopNav>

        {/* RIGHT: CTA (desktop) + mobile controls */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifySelf: "end",
          }}
        >
          {/* use react-router Link by rendering ContactCTA as Link */}
          <ContactCTA as={Link} to="/contact" aria-label="Contact us">
            <span
              className="cta-icon"
              aria-hidden
              style={{
                color: theme.colors?.ctaIconStroke || "#021025",
                display: "inline-grid",
                placeItems: "center",
              }}
            >
              <Mail size={16} strokeWidth={1.5} />
            </span>

            <span className="cta-text">
              <span className="cta-line1">Get a proposal</span>
              <span className="cta-line2">Talk to sales</span>
            </span>
          </ContactCTA>

          {/* mobile controls (visible only on small screens) */}
          <MobileControls>
            <NavButton
              aria-label="Toggle menu"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              <svg
                width="22"
                height="14"
                viewBox="0 0 22 14"
                fill="none"
                aria-hidden
              >
                <rect width="22" height="2" rx="1" y="0" fill="currentColor" />
                <rect width="22" height="2" rx="1" y="6" fill="currentColor" />
                <rect width="22" height="2" rx="1" y="12" fill="currentColor" />
              </svg>
            </NavButton>
          </MobileControls>
        </div>
      </HeaderInner>

      {/* Mobile menu: nav links with icons + CTA at bottom */}
      <MobileMenu open={open} role="menu" aria-hidden={!open}>
        {links.map((l, i) => {
          const Icon = mobileIcons[i] || Clipboard;
          return (
            <MobileLink
              key={l.id}
              to={l.href}
              onClick={() => setOpen(false)}
              role="menuitem"
              aria-label={l.label}
            >
              <Icon
                size={18}
                strokeWidth={1.6}
                color={theme.colors?.muted || "rgba(230,240,255,0.8)"}
                aria-hidden
              />
              <span>{l.label}</span>
            </MobileLink>
          );
        })}

        <div style={{ display: "flex", justifyContent: "center" }}>
          <ContactCTA2 as={Link} to="/contact" onClick={() => setOpen(false)}>
            <span
              className="cta-icon"
              aria-hidden
              style={{
                color: theme.colors?.ctaIconStroke || "#021025",
                display: "inline-grid",
                placeItems: "center",
              }}
            >
              <Mail size={16} strokeWidth={1.5} />
            </span>

            <span className="cta-text">
              <span className="cta-line1">Get a proposal</span>
              <span className="cta-line2">Talk to sales</span>
            </span>
          </ContactCTA2>
        </div>
      </MobileMenu>
    </HeaderWrap>
  );
}
